import { MenuProps } from "src/assets/imports/antdComponents";

export interface AskKuralModalProps {
	isModalOpen: boolean;
	handleOk: () => void;
	handleCancel: () => void;
}

export enum StudyType {
	Transcripts = "transcripts",
	Reports = "reports",
	Notes = "notes",
	Quant = "quant",
	Auto = "auto",
	Simulation = "simulation",
	Web_Boards = "web_boards",
	PXB = "pxb",
}

// The central harness backend only knows how to dispatch these four sub-agents
// (qual / quant / reports / notes). Any other study types must NOT be sent in
// harness_filters or the backend rejects the request with "Invalid search type".
export const HARNESS_SUPPORTED_STUDY_TYPES: StudyType[] = [
	StudyType.Transcripts,
	StudyType.Quant,
	StudyType.Reports,
	StudyType.Notes,
];

export enum AskKuralStudyTypeMapping {
	market_research = "transcripts",
	quant = "quant",
	notes = "notes",
	pxb = "pxb",
	web_boards = "web_boards",
	reports = "reports",
	simulation_message_testing = "simulation",
}

export enum AskKuralModeType {
	Fast = "Fast",
	Reflective = "Reflective",
	Widget = "Widget",
}

export enum AskKuralUserMode {
	Auto = "auto",
	Custom = "custom",
}

export enum StudyTypeDisplayName {
	"transcripts" = "Qualitative",
	// "transcripts" = "Bespoke Insights",
	"reports" = "Reports",
	"notes" = "Research Notes",
	"web_boards" = "Web Boards",
	"quant" = "Quantitative",
	"auto" = "Answer",
	"pxb" = "PXB",
	// "quant" = "Bank of Standards",
	"simulation" = "Simulation",
}

export enum StudyTypeDisplayName2 {
	"transcripts" = "Transcript",
	"reports" = "Report",
	"notes" = "Note",
	"quant" = "Quant",
	"auto" = "Answer",
	"simulation" = "Simulation",
}

export enum QueriesConfigType {
	AskKural = "ask-kural",
	Cognitive = "cognitive",
}
export enum QueryMessageType {
	sent = "sent",
	received = "received",
}
export enum AskKuralVersionType {
	v1 = "v1.0",
	v2 = "v2.0",
	v3 = "v3.0",
	v3_1 = "v3.1",
	v3_2 = "v3.2",
	v3_3 = "v3.3",
	v4 = "v4.0",
}

export enum ReferenceDrawerVisibilityType {
	hidden = "drawer-hidden",
	expanded = "expanded",
	shiftedExpanded = "shifted-expanded",
}

export interface IFeatureVersions {
	[key: string]: string;
}

export interface IStudyTypeContentProps {
	index: number;
	studyType: string;
	displayText: string;
	components: any;
	queryId?: string;
	combinedMessage: any;
}

export interface IMessageContentProps {
	text: string;
	components?: any;
	queryId?: string;
	studyType?: StudyType;
	combinedMessage?: any;
	index?: number;
	references?: any;
	messageId?: string;
	isReferenceLoading?: boolean;
	isReferenceTerminal?: boolean;
}

export interface ICombinedMessage {
	queryId?: string;
	message_id?: string;
	streamMessageId?: string;
	messageId?: { [key: string]: string };
	// Ghost branch_id per harness sub-agent tab. Populated from each
	// `stream_event`. Used to fetch the persisted answer via
	// /chat/m_chat/detail when a tab whose run has completed is clicked.
	subAgentBranchId?: { [key: string]: string };
	agentMode?: { [key: string]: string };
	ratingIdForApi?: string;
	type: QueryMessageType;
	isGenerating?: boolean;
	ratingGiven?: number;
	ratingDesc?: string;
	text: { [key: string]: string } | string;
	is_notification?: { [key: string]: boolean };
	interruptElements?: { [key: string]: any[] };
	isInterrupt?: { [key: string]: boolean };
	isStreamComplete?: { [key: string]: boolean };
	isCountFollowUp?: boolean;
	is_thinking?: { [key: string]: boolean };
	isDeepResearchMode?: boolean;
	thinking_events?: { [key: string]: string } | string;
	is_thinking_end?: { [key: string]: boolean };
	references?: {
		[studyType: string]: IMessageReferences;
	};
	referencesResolved?: {
		[studyType: string]: boolean;
	};
	/**
	 * Real-time per-namespace references accumulated from `subagent_references`
	 * SSE events emitted by the harness stream as each subagent completes.
	 * Shape: { [namespace: string]: { [turnDocKey: string]: ICitationReference } }
	 * e.g. { "qual_1": { "turn1doc0": <ref> }, "reports_2": { "turn2doc3": <ref> } }
	 */
	harnessNamespaceReferences?: {
		[namespace: string]: {
			[turnDocKey: string]: ICitationReference;
		};
	};
	isHarnessTurn?: boolean;
	harnessMessageId?: string;
	finalAnswerAgentType?: string;
	finalAnswerStreamId?: string;
	hasError?: Record<string, boolean>;
	errorMessage?: Record<string, string>;
	isAnswerReady?: boolean;
	frameworkSummaryStarted?: Record<string, boolean>;
	agentRunStatus?: Record<string, string>;
	total_time_elapsed?: { [key: string]: number };
	thinking_start_time?: { [key: string]: string };
	deck?: {
		_id?: string;
		deck_id: string;
		deck_title: string;
		stage: string;
		status?: string;
		ppt_type?: string;
		message_id?: string;
		branch_id?: string;
		deck_plan?: { slide_idx: number; story: string }[];
		plan?: { slide_idx: number; story: string }[];
	};
}

export type MenuItem = Required<MenuProps>["items"][number];

export interface IQuery {
	id: string;
	name: string;
	file_id: string[];
	meta_id: string[];
	entity_id: string[];
	search_type: string;
	messages?: { text: string; type: QueryMessageType }[];
	result_rating_desc?: string;
	rating?: number;
	ReferenceItems?: IReferenceItem[];
	isSaved?: boolean;
}

export interface IChatPanelItem {
	chat_id: string;
	chat_name: string;
	branch_id?: string;
	is_chat_available?: boolean;
}

export interface IChatData {
	chat_id: string;
	chat_name: string;
	version?: AskKuralVersionType;
	branch_id?: string;
	created_by?: {
		user_id?: string;
		user_name?: string;
		user_email?: string;
	};
	created_at?: string;
	updated_by?: {
		user_id?: string;
		user_name?: string;
		user_email?: string;
	};
	last_updated_at?: string;
	references?: { [key: string]: IReferenceItem[] };
	filters?: {
		entity_id?: string[];
		file_id?: string[];
		meta_id?: string[];
		search_type?: StudyType;
		all_search_type?: StudyType[];
		chat_language?: string;
		user_mode?: AskKuralUserMode;
	};
	queries?: IChatQuery[];
	is_chat_available?: boolean;
	path?: Array<{
		message_id: string;
		query_id?: string;
		query_dt: string;
		message_type: "user" | "kural";
		message_content: string;
		search_type?: string;
		feedback?: {
			rating: number | null;
			message: string | null;
		};
		references?: IMessageReferences;
	}>;
	mode?: ReflectiveChatType;
}

export enum ReflectiveChatType {
	Deep_Research = "Deep_Research",
	Normal = "normal",
}

export enum ChatType {
	"Deep_Research" = "Deep_Research",
	"Counts_widget" = "counts_widget",
	"Quotes_widget" = "quotes_widget",
	"Reflective" = "Reflective",
	"Fast" = "Fast",
}

export interface IChatData2 {
	chat_id: string;
	branch_id?: string;
	created_by?: {
		user_id?: string;
		user_name?: string;
		user_email?: string;
	};
	created_at?: string;
	last_updated_at?: string;
	filters?: {
		entity_id?: string[];
		file_id?: string[];
		meta_id?: string[];
		all_search_type?: string[];
		chat_language?: string;
	};
	path: {
		message_id: string;
		query_id?: string;
		query_dt: string;
		message_type: "user" | "kural";
		message_content: string;
		search_type?: string[];
		source_type?: string;
		feedback?: {
			rating: number | null;
			message: string | null;
		};
	}[];
	references?: IReferenceItem[];
	total_token_count?: number;
}

export interface IChatQuery {
	query_id: string;
	query_dt: string;
	query: string;
	query_response: string;
	feedback: {
		rating: number;
		message: string;
	};
	message_id?: string;
}
export interface IChatContainerProps {
	messages: IQueryMessage[];
	ratingGiven?: number;
	ratingDesc?: string;
	isLoading: boolean;
	isProcessComplete: boolean;
	referenceItems: IReferenceItem[];
	isSaved?: boolean;
	setIsDrawerVisible: React.Dispatch<React.SetStateAction<boolean>>;
	isDrawerVisible: boolean;
	onSave: () => void;
	onSubmitRating: (rating: number, comment: string) => Promise<void>;
	isExpanded: boolean;
	setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

export interface IQueryMessage {
	queryId?: any;
	ratingGiven?: number;
	ratingDesc?: string;
	text: string;
	type: QueryMessageType;
	is_notification?: boolean;
	sectionId?: string;
	interruptElements?: any[];
	isInterrupt?: boolean;
	order?: number | string;
	is_error?: boolean;
}

export interface IStreamResponse {
	event: string;
	id?: string;
	data: string;
}

export interface IAskKuralSidebarProps {
	loading: boolean;
	setLoading: (value: boolean) => void;
	reset: number;
	setReset: React.Dispatch<React.SetStateAction<number>>;
	combinedMessages: ICombinedMessage[];
	setCombinedMessages: React.Dispatch<React.SetStateAction<ICombinedMessage[]>>;
	setSelectedTags: React.Dispatch<React.SetStateAction<[]>>;
	updatedChatName: string;
	setUpdatedChatName: React.Dispatch<React.SetStateAction<string>>;
	openAskKuralSidebar: () => void;
}

export interface IRightPanelProps {
	loading: boolean;
	setLoading: (value: boolean) => void;
	dropdownData: {
		transcripts: any[];
		reports: any[];
		notes: any[];
	};
	// transcripts: any[];
	// reports: any[];
	// notes: any[];
	resetKey: number;
}

export interface IReferenceItemProps {
	item: IReferenceItem;
	onTranscriptClick: (item: IReferenceItem) => void;
	activeCitationNo: number;
	activeTab: StudyType;
	handleFileDownload?: (item: IReferenceItem) => void;
}

export interface ITranscriptDataInter {
	numberOfPatients?: number;
	numberOfSearchResults?: number;
	emotionsCounts: any;
	statesResults: any;
	gptSummary?: any;
	gptSummaryText?: string;
	headers?: any;
	searchResults: {
		id: number;
		s3path: string;
		callDate: string;
		callId: string;
		callTime: string;
		callTypeIds: string;
		content?: string;
		context: string;
		context_preview: string;
		patientId: number;
		dialogueNo: number;
		name: string;
		tags: string[];
		dialogueAppearedAt: number;
		dialogueLength: number;
		transcriptLength: number;
		addedToStory: boolean;
		storiesAdded: {
			storyId: any;
			storyName: string;
		}[];
		speaker: string;
		elasticSearchId: string;
		transcripts: {
			dialogueNo: number;
			speaker: string;
			text: string;
		}[];
		storyDialogues: any;
		wave: string;
		geography: string;
		fileName: string;
		source: string;
		studyName: string;
		studyId: number;
		FileType?: string;
	}[];
}

export interface ITranscriptItem {
	time?: string;
	dialog_id: number;
	speaker_type: string;
	dialog: string;
}

export interface IReferenceItem {
	context?: IDialogueItem[];
	context_preview: IDialogueItem[];
	file_name: string;
	file_type: string;
	entity_id?: string;
	entity_name?: string;
	tr_id?: string;
	tags: any[];
	conversation_id?: number;
	citation_id?: number;
	transcriptItems?: ITranscriptItem[];
	onTranscriptClick?: (transcript: IReferenceItem) => void;
	report_id?: string;
	document_id?: number;
	pdf_url?: string;
	cimg_path_url?: string;
	is_study_access_available: boolean;
	display_message?: string;
	study_language?: {
		name: string;
		code: string;
	};
	first_dialog_no?: string | null;
	page_no?: number;
	highlighted_dialog_nos?: Array<{
		dialog_no: string;
		dialog_highlights: Array<{
			start_index: string;
			end_index: string;
		}>;
	}>;
	context_preview_image?: {
		image_path: string;
	} | null;
	notes_text?: string;
	is_audio_video?: boolean;
	media_url?: string;
	media_type?: MediaType | null;
	media_duration?: string;
	media_duration_seconds?: number;
	file_id?: string;
}

export interface IReferenceDrawerProps {
	isExpanded: boolean;
	onClose: () => void;
	setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
	referenceItems: IReferenceItem[];
	activeCitation: { [studyType: string]: number | null };
	setActiveCitation?: (value) => void;
	calculateScrollRatio: () => void;
	referenceDrawerVisibility: ReferenceDrawerVisibilityType;
	referenceActiveTab: StudyType;
	studyType?: StudyType[];
}

export interface IDialogueItem {
	dialog_no?: number;
	speaker_type?: string;
	text: string;
	dialog?: string;
	page_no?: number;
}

export interface IMenuInfo {
	key: string;
	domEvent: React.MouseEvent<HTMLElement>;
}

interface ITagType {
	value: string;
	label: string;
}

export interface ITagDisplayProps {
	tags: ITagType[];
	highlightText: string;
	maxVisibleTags?: number;
	truncateLength?: number;
	isStudyLevel?: boolean;
	isChecked?: boolean;
}

export interface ISearchBarProps {
	value: string;
	onChange: (value: string) => void;
	onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export interface IDataset {
	key: string;
	label: string;
	tags: Array<{ value: string; label: string; category: string }>;
}

export interface IStudy {
	study_id: string;
	study: string;
	tags: Array<{ value: string; label: string; category: string }>;
	datasets: IDataset[];
}

export interface IDataConfig {
	[key: string]: IStudy[];
}

export interface ITag {
	value: string;
	label: string;
	category: string;
}

export interface IOptionData {
	label: string;
	value: string;
	key: string;
}

export interface IOptionGroup {
	label: string;
	title: string;
	options: IOptionData[];
}

export interface IHighlightedDialog {
	dialog_no: string;
	dialog_highlights: Array<{
		start_index: string;
		end_index: string;
	}>;
}

export interface ITranscriptViewProps {
	transcriptName: string;
	createdBy: string;
	transcriptItems?: ITranscriptItem[];
	onClose: () => void;
	isLoadingTranscript: boolean;
	contextIds: number[];
	isExpanded: boolean;
	handleExpand: () => void;
	module?: string;
	highlightedDialogs?: IHighlightedDialog[];
	firstDialogNo?: string | null;
}
export interface IReportViewProps {
	reportName: string;
	pdfUrl: string;
	onClose: () => void;
	loading?: boolean;
	setLoading: React.Dispatch<React.SetStateAction<boolean>>;
	pageNo: number;
	isExpanded: boolean;
	handleFileDownload: (item: IReferenceItem) => void;
	handleExpand: () => void;
}

export interface IFlattenedJsonItem {
	display: string;
	id: string;
}

export interface IRatingPopoverContentProps {
	queryId: string;
	initialComment: string;
	ratingIdForApi: string;
	onSubmit: (queryId: string, ratingIdForApi: string, comment: string) => void;
	validateComment: (value: string, setError: React.Dispatch<React.SetStateAction<string>>) => boolean;
	ratingLoading: boolean;
}

export interface IDynamicQuantTreeSelectProps {
	allData: any[];
	selectedData: any[];
	setSelectedData: (data: any[]) => void;
	placeholder: string;
}

export interface IMultiSelectStudyDropdownProps {
	availableStudyTypeDropdownItems: { label: JSX.Element; value: string; disabled: boolean }[];
	selectedType: string[];
	handleTypeChange: (value: StudyType[]) => void;
	isCompDisabled: boolean;
	isLoading: boolean;
	disabled: boolean;
	multiSelectLabel?: string;
	disabledItems?: Array<{ value: string; message: string }>;
	isHomePage?: boolean;
}

export interface IWidgetDropdownDataItem {
	title: string;
	key: string;
	value: string;
	id?: string;
	children?: IWidgetDropdownDataItem[];
	is_segment?: boolean;
	is_question?: boolean;
	is_numerical?: boolean;
	question_text?: string;
}

export interface IWidgetDropdownData {
	split_by?: IWidgetDropdownDataItem[];
	add_filter?: IWidgetDropdownDataItem[];
	x_axis_field?: IWidgetDropdownDataItem[];
}

export enum QuantComponentsRenderedFromType {
	Quant = "quant",
	QuantFullscreen = "fullscreen",
	Widget = "widget",
}

export enum WidgetType {
	Chart = "chart",
	Quant = "quant",
	Qual = "qual",
	Counts = "counts",
	Quotes = "quotes",
	Deepdive = "deepdive",
	NotesCounts = "notes-counts",
}

export enum QualWidgetExportType {
	Quotes = "quotes-widget",
	Counts = "counts-widget",
	Deepdive = "deepdive-widget",
	AgenticCounts = "agentic-counts-widget",
	AgenticWidget = "agentic-widget",
	NotesCounts = "notes-counts-widget",
}

export const WidgetTypeMap: Record<string, string> = {
	chart: "quantChartWidget",
	quant: "quantChartWidget",
	qual: "qualWidget",
	counts: "countWidget",
	quotes: "quotesWidget",
	inlineReferences: "inlineReferences",
	inlineWidget: "inlineReferences",
	"notes-counts": "countWidget",
};

export interface INotesCountTag {
	tag_category: string;
	tag_value: string;
}

export interface INotesCountReference {
	notes_id: string;
	notes_element_id: string;
	notes_text: string;
	labels: string[];
	tags: INotesCountTag[];
	relevance_score?: number | null;
}

export interface INotesCountWidgetData {
	denominator: number;
	reworded_query: string;
	study_id: string;
	study_name: string;
	labels: string[];
	references: INotesCountReference[];
	info_message?: string;
	relevant_metadata?: string[];
	relevant_metadata_rationale?: string;
	themes_generated?: boolean;
	label_descriptions?: Record<string, string>;
	/** True when labels are mutually exclusive and collectively exhaustive.
	 *  Drives whether the breakdown's collapsed view shows stacked label-
	 *  composition bars (MECE) or falls back to a flat top-N summary. */
	is_mutually_exclusive?: boolean;
	selected_metadata_filters?: ISelectedMetadataFilter[] | null;
}

export enum CountWidgetState {
	Active = "active",
	Stale = "stale",
}

export enum MessageElementType {
	Markdown = "markdown",
	Widget = "widget",
	Error = "error",
}

export interface MarkdownElement {
	type: MessageElementType.Markdown;
	content: string;
	key: string;
}

export interface WidgetElement {
	type: MessageElementType.Widget;
	widgetInfo: {
		type: string;
		widget_data: any;
	};
	key: string;
}

export interface WidgetErrorElement {
	type: MessageElementType.Error;
	key: string;
}

export type MessageElement = MarkdownElement | WidgetElement | WidgetErrorElement;

export interface IAgenticAskKuralResponse {
	message?: string;
	success?: boolean;
	data?: {
		query_id?: string;
		message_id?: string;
	};
	error?: string | null;
}

export interface IFollowUps {
	[key: string]: string[];
}
export interface IFilterOption {
	tag_value: string;
}

export interface ISelectedMetadataFilter {
	tag_category: string;
	options: IFilterOption[];
}

export interface IInlineWidgetData {
	widgetId?: string;
	tool_tip?: IWidgetToolTip;
	context_preview?: string;
	widget_type?: string;
}

export interface IWidgetToolTipTopLabel {
	count?: number;
	name?: string;
}

export interface IWidgetToolTip {
	denominator?: number;
	labels_gt_3?: boolean;
	query?: string;
	study_name?: string;
	labels?: IWidgetToolTipTopLabel[];
	selected_metadata_filters?: ISelectedMetadataFilter[] | null;
}
export interface ICitationReference {
	citation_type: "transcripts" | "reports" | "notes" | string;
	tr_id?: string;
	order?: number | string;
	entity_id?: string;
	notes_id?: string;
	notes_element_id?: string;
	entity_name?: string;
	file_name: string;
	file_type: string;
	tags?: Array<{
		meta_id: string;
		tag_category: string;
		tag_value: string;
	}>;
	context_preview?:
		| {
				dialog_no: number;
				text: string;
		  }
		| null
		| string;
	first_dialog_no?: string | null;
	highlighted_dialog_nos?: Array<{
		dialog_no: string;
		dialog_highlights: Array<{
			start_index: string;
			end_index: string;
		}>;
	}>;
	is_study_access_available?: boolean;
	// For reports/PDFs
	pdf_url?: string;
	page_no?: number;
	tool_tip?: IWidgetToolTip | null;
	widget_type?: string;
	context_preview_image?: {
		image_path: string;
	} | null;
	notes_text?: string;
	is_audio_video?: boolean;
	media_url?: string;
	media_type?: MediaType | null;
	media_duration?: string;
	media_duration_seconds?: number;
	file_id: string;
}

export interface ICitationData {
	citation_id: string;
	citation_type: string;
	citation_transcripts_data?: ICitationReference[];
	citation_reports_data?: ICitationReference[];
	// Add other citation types as needed
}

// Message-level references structure (from API)
export interface IMessageReferences {
	[citeKey: string]: {
		[turnDocKey: string]: ICitationReference;
	};
}

export interface ICitationTagProps {
	citationId: string;
	studyType: string;
	messageId?: string;
	references?: ICitationReference[];
	isReferenceLoading?: boolean;
	isReferenceTerminal?: boolean;
	/** Per-message completion override for the citation loader (see useCitationLoaderState). */
	isProcessComplete?: boolean;
	expectedReferenceCount?: number;
	onCitationClick?: (citationId: string, studyType: string, references: ICitationReference[], messageId: string, scrollUniqueId?: string) => void;
}

export interface IReflectiveReferenceItemProps {
	item: IReferenceItem;
	onTranscriptClick: (item: IReferenceItem) => void;
	activeCitationNo: number;
	activeTab: StudyType;
	handleFileDownload?: (item: IReferenceItem) => void;
	isAudioVideoEnabled?: boolean;
}

// Type definitions for Thinking Mode component

export interface ThinkingEvent {
	heading: string;
	summary: string;
	status: "in-progress" | "completed" | "pending";
	time_elapsed: number; // in seconds
	progress_state: number; // 0-100
}

export type ThinkingStatus = "thinking" | "completed";

export interface ThinkingWindowProps {
	thinkingEvents: ThinkingEvent[];
	thinkingEnd: boolean;
	totalElapsedTime?: number;
	initialExpanded?: boolean;
	onToggle?: (expanded: boolean) => void;
	className?: string;
	thinkingStart?: boolean;
	components?: any;
	studyType?: StudyType;
	queryId?: string;
	combinedMessage?: any;
	index?: number;
	total_time_elapsed?: number;
	thinking_start_time?: string;
}

export interface ThinkingHeaderProps {
	thinkingEnd: boolean;
	elapsedTime: string;
	isExpanded: boolean;
	onToggle: () => void;
	overallProgress: number;
	thinkingStart: boolean;
	thinkingEvents: ThinkingEvent[];
	liveElapsedSeconds?: number;
}

export interface ThinkingTimelineProps {
	events: ThinkingEvent[];
	isVisible: boolean;
	components?: any;
	studyType?: StudyType;
	queryId?: string;
	combinedMessage?: any;
	index?: number;
}

export interface ThinkingEventProps {
	event: ThinkingEvent;
	isLast: boolean;
}
export interface ITranscriptPreviewPopoverProps {
	references: ICitationReference[];
	onViewDetails?: (referenceItem: ICitationReference) => void;
}

export interface IChartData {
	color: string;
	count: number;
	hexColor: string;
	label: string;
	percentage: number;
}

export interface IAgenticInlineWidgetPopoverProps {
	widgetData?: IInlineWidgetData[];
	references?: ICitationReference[];
	studyType?: string;
	onViewDetails?: (referenceItem: IInlineWidgetData) => void;
	chartData?: IChartData[];
	TopDataIntoChartFormat?: { [key: string]: IChartData[] };
}

export enum TIMING {
	SCROLL_DELAY = 150,
	NAVIGATION_DEBOUNCE = 800,
	COPY_FEEDBACK_DURATION = 2000,
}

// Mode-specific filter state
export interface IModeFilters {
	selectedTypes: string[];
	selectedStudies: string[];
	selectedStudiesMap: { [key: string]: string[] };
	selectedData: string[];
	selectedDataMap: { [key: string]: string[] };
	selectedTags: string[];
}

export interface IRatingModalProps {
	visible: boolean;
	onClose: () => void;
	onSubmit: (rating: number, options: string[], otherText?: string) => void;
	title: string;
	question: string;
	initialRating?: number;
	loading?: boolean;
	initialOptions?: string[];
	initialOtherText?: string;
	isStandardQueryMode?: boolean;
}

export interface IFeedbackOption {
	key: string;
	label: string;
}

// Types
export interface IStudy {
	study_id: string;
	study: string;
	selectedType: string;
	insights_generated?: boolean;
	tags?: Array<{ label: string }>;
	datasets?: Array<{
		key: string;
		label: string;
		tags?: Array<{ label: string }>;
	}>;
	folder_id?: string;
	folder_name?: string;
}

export interface ICollection {
	folder_id: string;
	folder_name: string;
	studies: IStudy[];
}

export interface IOrganizedData {
	collections: ICollection[];
	standaloneStudies: IStudy[];
}

export interface ISelectStudiesSideDrawerProps {
	loading: boolean;
	dataConfig: Record<string, IStudy[]>;
	selectedType: string[];
	chatStarted: boolean;
	catOptions: any[];
	selectedTags: any[];
	setSelectedType: (types: string[]) => void;
	setSelectedTags: (tags: any[]) => void;
	availableStudyTypeDropdownItems: Array<{ value: string; label: string }>;
	isHarnessUserModeEnabled?: boolean;
	allowChangesAfterChatStart?: boolean;
}

export const OTHER_RATING_OPTION = {
	key: "Other",
	label: "Other",
};

// Feedback options for different rating bands
export const LOW_RATING_OPTIONS: IFeedbackOption[] = [
	{ key: "Did not follow instructions", label: "Did not follow instructions" },
	{ key: "Insights were inaccurate", label: "Insights were inaccurate" },
	{ key: "Used the wrong data", label: "Used the wrong data" },
	{ key: "Response lacked clear structure", label: "Response lacked clear structure" },
	{ key: "Lacked quantification", label: "Lacked quantification" },
	OTHER_RATING_OPTION,
];

export const MEDIUM_RATING_OPTIONS: IFeedbackOption[] = [
	{ key: "Be more concise", label: "Be more concise" },
	{ key: "Respond faster", label: "Respond faster" },
	{ key: "Add more quotes", label: "Add more quotes" },
	{ key: "Formatting and structure", label: "Formatting and structure" },
	{ key: "Include more quantification", label: "Include more quantification" },
	OTHER_RATING_OPTION,
];

export const HIGH_RATING_OPTIONS: IFeedbackOption[] = [
	{ key: "Accurate insights", label: "Accurate insights" },
	{ key: "Followed instructions", label: "Followed instructions" },
	{ key: "Understood my intent", label: "Understood my intent" },
	{ key: "Citations/Evidence-backed", label: "Citations/Evidence-backed" },
	{ key: "Clear formatting and structure", label: "Clear formatting and structure" },
	OTHER_RATING_OPTION,
];

export const REFLECTIVE_LOW_RATING_OPTIONS: IFeedbackOption[] = [
	{ key: "Did not follow instructions", label: "Did not follow instructions" },
	{ key: "Insights were inaccurate", label: "Insights were inaccurate" },
	{ key: "Used the wrong data", label: "Used the wrong data" },
	{ key: "Response lacked clear structure", label: "Response lacked clear structure" },
	{ key: "Lacked quantification", label: "Lacked quantification" },
	OTHER_RATING_OPTION,
];

export const REFLECTIVE_MEDIUM_RATING_OPTIONS: IFeedbackOption[] = [
	{ key: "Be more concise", label: "Be more concise" },
	{ key: "Respond faster", label: "Respond faster" },
	{ key: "Add more quotes", label: "Add more quotes" },
	{ key: "Formatting and structure", label: "Formatting and structure" },
	{ key: "Include more quantification", label: "Include more quantification" },
	OTHER_RATING_OPTION,
];

export enum RatingQues {
	Rate_1_2 = "What didn't work?",
	Rate_3_4 = "How can we improve?",
	Rate_5 = "What worked well?",
}

export enum RateBaseHeader {
	RateHeader = "Rate this response",
	RateHelpfulHeader = "How helpful was this response?",
	ReflectiveRateHeader = "Rate your Reflective Mode experience",
	ReflectiveRateQuestion = "How was your Reflective Mode chat experience?",
}

export enum RatingQuesPlaceholder {
	Rate_1_2 = "Describe what didn't work out for you",
	Rate_3_4 = "Describe how can we improve",
	Rate_5 = "Describe what worked well",
}

export interface IModeSelectionDropdownProps {
	disabled?: boolean;
	chatStarted?: boolean;
	showDot: boolean;
	showBetaTag: boolean;
	onModeChange: (mode: AskKuralModeType, widgetType?: string) => void;
	isTranscriptsAvailable: boolean;
}

export interface IQuantCitationRef {
	order?: string | number;
	/** Whether this citation should auto-expand inline in the message */
	is_inline?: boolean;
	chart_bypass_data?: Record<string, any>;
	analysis_bypass_data?: Record<string, any>;
	analysis_rows?: any[];
	question_id?: string;
	fe_payload?: IQuantFePayload;
}

export interface IQuantFePayload {
	analysis_id: string;
	chart_title: string;
	chart_subtitle: string;
	chart_type: string;
	analysis_type: string;
	is_grid: boolean;
	is_weight: boolean;
	is_splitting: boolean;
	ai_header?: string;
	question_number: string;
	active_insights_id: string;
	footnote: {
		q_txt: string;
		src: string;
		other: string;
		log_message: string | null;
		stat_message?: string;
	};
	axis_idf: {
		x: string | null;
		y: string | null;
		z1: string | null;
		z2: string | null;
	};
	filter_hierarchy: any[];
	data: any[];
	chart_config: Record<string, any>;
	include_blanks: boolean;
	x_axis_values: any[];
	y_axis_values: any[];
	x_axis_label: string;
	y_axis_label: string;
	position: { transpose: boolean };
	value_params: any[];
}

export interface IQuantCitationDrawerProps {
	quantCitationData: IQuantCitationRef | null;
	onClose: () => void;
	isExpanded: boolean;
	handleExpand: () => void;
}
export interface IReflectiveNotesReferenceItemProps {
	item: IReferenceItem;
	onTranscriptClick: (item: IReferenceItem) => void;
	activeCitationNo: number | null;
	activeTab: StudyType;
}

export interface ITextHighlight {
	start_index: number;
	end_index: number;
}

export interface INotesContextPreview {
	text: string;
	text_highlights: ITextHighlight[];
}
export interface IAudioVideoTranscriptItem {
	_id?: string;
	dialog_id: number;
	speaker_label: string;
	speaker_type: string;
	dialog: string;
	timestamp_start: number | null;
	timestamp_end: number | null;
	context: string | null;
}

export interface IAudioVideoNarration {
	narration_id: number;
	content: string;
	frame: string | null;
	img_url: string | null;
	next_dialog_id: number;
}

export enum MediaType {
	Audio = "audio",
	Video = "video",
}

export interface IAudioVideoUploadDetails {
	transcript_name: string;
	uploaded_date: string;
	duration: string; // e.g. "10:53"
	duration_seconds: number;
	media_url: string;
	media_type: MediaType;
}
export interface IAudioVideoTranscriptViewProps {
	item: IReferenceItem & { created_at?: string };
	onClose: () => void;
	isExpanded: boolean;
	handleExpand: () => void;
	transcriptItems: IAudioVideoTranscriptItem[];
	mediaUrl: string;
	mediaType?: MediaType;
	isLoadingAV: boolean;
	highlightedDialogs?: IHighlightedDialog[];
	firstDialogNo?: string | null;
	presignedSegmentUrls?: string[];
	onDownloadTranscript?: () => void;
	narrations?: IAudioVideoNarration[];
}
export interface IVideoPlayerProps {
	mediaUrl: string;
	posterUrl?: string;
	type?: string;
	duration?: string;
	durationSeconds?: number;
	onTimeUpdate?: (currentTime: number) => void;
	onPlayStateChange?: (isPlaying: boolean) => void;
	onError?: (err?: DOMException | Event) => void;
	onReady?: () => void;
	seekToTime?: number;
	seekTrigger?: number;
	onSeeked?: (currentTime: number) => void;
	pauseTrigger?: number;
	playTrigger?: number;
	presignedSegmentUrls?: string[];
	ariaLabel?: string;
}
export interface IAudioPlayerProps {
	mediaUrl: string;
	duration?: string;
	durationSeconds?: number;
	onTimeUpdate?: (currentTime: number) => void;
	onPlayStateChange?: (isPlaying: boolean) => void;
	onError?: () => void;
	onReady?: () => void;
	seekToTime?: number;
	seekTrigger?: number;
	onSeeked?: (currentTime: number) => void;
	pauseTrigger?: number;
	playTrigger?: number;
	presignedSegmentUrls?: string[];
}

export enum PPTExportSourceType {
	Dashboard = "dashboard",
	AskKural = "ask-kural",
}

export interface IAskKuralPPTFootnote {
	q_txt?: string;
	src?: string;
	notes?: string;
	other?: string;
	log_message?: string | null;
}

export interface IAskKuralPPTAxisIdf {
	x: string | null;
	y: string | null;
	z1: string | null;
	z2: string | null;
}

export interface IAskKuralPPTFilterHierarchyItem {
	dimension: string;
	order?: number;
	orientation?: string;
	values: string[];
}

export interface IAskKuralPPTSeriesPoint {
	name: string;
	originalName: string;
	value: number | string;
	n_size: number | string;
}

export interface IAskKuralPPTChartRow {
	xAxis: string;
	xAxisOriginal: string;
	series: IAskKuralPPTSeriesPoint[];
	n_size: { min: number; max: number };
}

export interface IAskKuralPPTDataEntry {
	chart_data: IAskKuralPPTChartRow[];
}

export interface IAskKuralPPTChartItem {
	analysis_id: string;
	ai_header?: string;
	chart_title: string;
	chart_subtitle?: string;
	chart_type: string;
	analysis_type?: string;
	is_grid?: boolean;
	is_weight?: boolean;
	is_splitting?: boolean;
	question_number?: string;
	insights_id?: string;
	footnote?: IAskKuralPPTFootnote;
	axis_idf?: IAskKuralPPTAxisIdf;
	filter_hierarchy?: IAskKuralPPTFilterHierarchyItem[];
	data: IAskKuralPPTDataEntry[];
	chart_config?: Record<string, unknown>;
	include_blanks?: boolean;
	x_axis_values?: unknown[];
	y_axis_values?: unknown[];
	x_axis_label?: string;
	y_axis_label?: string;
	position?: { transpose: boolean };
	value_params?: unknown[];
}

export interface IAskKuralPPTExportPayload {
	source: PPTExportSourceType.AskKural;
	combined: IAskKuralPPTChartItem[];
}

/* ---------------------------------------------
 Token Usage Governance — raw BE shapes
 (FE-internal model lives in src/pages/NewAskKural/governance/types.ts)
----------------------------------------------*/

export type GenaiUsageStatus = "ok" | "warning" | "blocked";
export type GenaiUsageMode = "off" | "on" | "bypass";
/**
 * Wire enum for which axis of governance a usage entry / 429 detail tracks.
 * Optional on the wire for migration safety — older deploys that don't emit it
 * are treated as "dollars" so legacy behavior is preserved.
 */
export type GenaiUsageMetric = "dollars" | "queries";

export interface IGenaiUsageEntry {
	window: string;
	pct_used: number;
	status: GenaiUsageStatus;
	resets_at: string;
	metric?: GenaiUsageMetric;
}

export interface IGenaiUsageData {
	mode: GenaiUsageMode;
	warning_threshold_pct: number;
	usage?: IGenaiUsageEntry[];
}

export interface IGenaiUsageResponse {
	data: IGenaiUsageData;
}

export interface IGenai429Detail {
	message: string;
	window: string;
	limit: number;
	used: number;
	mode: "user" | "tenant";
	resets_at: string;
	warning_threshold_pct: number;
	metric?: GenaiUsageMetric;
}
